import { axiosInstance } from "./axiosInstance";
import { BoletoDeSubida } from "./classPortal.service";

/*
	Contenido del curso que heredan los horarios + pesos de la nota (Portal de Clase, Parte A2, 2026-10-17).
	Los mismos tres niveles que los pesos: curso (el origen) › módulo › horario. En cada nivel se pide lo mismo:
	  - curso / módulo (Catálogo › Cursos): /admin/classPortal/_catalogo/{curso|modulo}/{id}/_...
	  - horario (portal de la clase):       /admin/classPortal/{horario}/_content, _scheme
	Un override guarda solo lo que ese nivel cambió; "Volver A Lo Del Curso" lo quita.
*/

export type NivelContenido = "curso" | "modulo" | "horario"
export type TipoPieza = "unidad" | "tarea" | "material" | "quiz" | "pendiente" | "anuncio"
export type EntregaPieza = "foto" | "archivo" | "en_clase" | "sin_entrega"

export interface ArchivoPieza
{
	kind: "archivo" | "enlace"
	name: string
	mime: string | null
	size: number
	key: string | null
	url: string | null
}

export interface CriterioRubrica { name: string; points: number }

export interface PiezaContenido
{
	id: number
	origin: "curso" | "modulo" | "horario"
	originLabel: string
	type: TipoPieza
	sortOrder: number
	title: string | null
	body: string | null
	unitItemId: number | null
	publishSession: number | null
	publishTime: string | null
	dueSession: number | null
	dueTime: string | null
	dueBeforeClass: boolean
	delivery: EntregaPieza | null
	points: number | null
	category: string | null
	fixedWeightPct: number | null
	acceptLate: boolean
	latePenaltyPct: number
	rubric: CriterioRubrica[]
	files: ArchivoPieza[]
	hidden: boolean
	hiddenAbove: boolean
	changedHere: boolean
	changedFields: string[]
	changedAtModule: boolean
	isOwn: boolean
	publishLabel?: string | null
	publishPending?: boolean
	dueLabel?: string | null
	/** Tarea quitada del curso que ya tiene entregas: se queda hasta que alguien la oculte. */
	retired?: boolean
	/** Quiz del curso/módulo sin preguntas: no se publica a los horarios (decisión de Esteban, 2026-10-20). */
	noQuestions?: boolean
}

export interface AvisoContenido { type: "faltan" | "sinCategoria" | "sinSesion"; message: string; category?: string; missing?: number; item?: string }
export interface CategoriaEstadistica { name: string; weight: number; fixed: number; tasks: number; missing: number; esAsistencia: boolean }
export interface CategoriaPeso { name: string; weight: number; esAsistencia: boolean }

export interface ContenidoDelNivel
{
	level: NivelContenido
	id: number
	name: string
	canEdit: boolean
	items: PiezaContenido[]
	units: { id: number; title: string; origin: string; hidden: boolean }[]
	types: TipoPieza[]
	deliveries: EntregaPieza[]
	sessionsMax: number
	schedules: number
	errors: string[]
	warnings: AvisoContenido[]
	categories: CategoriaEstadistica[]
	scheme: { source: string; sourceLabel: string; categories: CategoriaPeso[]; total: number }
}

export interface EsquemaDelNivel
{
	level: NivelContenido
	id: number
	name: string
	canEdit: boolean
	own: { categories: CategoriaPeso[]; total: number } | null
	effective: { source: "horario" | "modulo" | "curso" | "sistema"; sourceLabel: string; categories: CategoriaPeso[]; total: number }
	allowTeacherOverride: boolean
	categoryStats: CategoriaEstadistica[]
	warnings: AvisoContenido[]
	errors: string[]
	maxCategories: number
}

/** Lo que se manda al crear o cambiar una pieza (camelCase, igual que lo que se recibe). */
export type DatosPieza = Partial<Omit<PiezaContenido, "id" | "origin" | "originLabel" | "sortOrder" | "hidden" | "hiddenAbove" | "changedHere" | "changedFields" | "changedAtModule" | "isOwn" | "files" | "publishLabel" | "publishPending" | "dueLabel" | "retired">> & {
	files?: ({ key: string; name: string; mime?: string | null; size?: number } | { link: string; name: string })[]
}

export interface ApiContenido
{
	nivel: NivelContenido
	id: number
	contenido(): Promise<ContenidoDelNivel>
	crear(d: DatosPieza): Promise<ContenidoDelNivel>
	cambiar(itemId: number, d: DatosPieza): Promise<ContenidoDelNivel>
	borrar(itemId: number): Promise<ContenidoDelNivel>
	ocultar(itemId: number, oculto: boolean, retirada?: boolean): Promise<ContenidoDelNivel>
	revertir(itemId: number): Promise<ContenidoDelNivel>
	ordenar(ids: number[]): Promise<ContenidoDelNivel>
	subida(d: { name: string; size: number; mime: string }): Promise<BoletoDeSubida>
	archivo(itemId: number, pos: number): Promise<{ kind: "archivo" | "enlace"; url: string; name: string; mime: string | null }>
	esquema(): Promise<EsquemaDelNivel>
	guardarEsquema(categorias: CategoriaPeso[], permiteMaestro?: boolean): Promise<EsquemaDelNivel>
	quitarEsquema(): Promise<EsquemaDelNivel>
	permiteMaestro(valor: boolean): Promise<EsquemaDelNivel>
}

export const apiContenido = (nivel: NivelContenido, id: number): ApiContenido =>
{
	const h = nivel === "horario"
	const b = h ? "/admin/classPortal/" + id : "/admin/classPortal/_catalogo/" + nivel + "/" + id
	const lista = h ? b + "/_content" : b + "/_contenido"
	const item = (i: number) => (h ? b + "/_content/" + i : b + "/_items/" + i)
	return {
		nivel, id,
		contenido: async () => (await axiosInstance.get(lista)).data,
		crear: async (d) => (await axiosInstance.post(b + "/_items", d)).data.content,
		cambiar: async (i, d) => (await axiosInstance.put(item(i), d)).data,
		borrar: async (i) => (await axiosInstance.delete(item(i))).data,
		ocultar: async (i, hidden, retired) => (await axiosInstance.post(item(i) + "/_hide", { hidden, retired: !!retired })).data,
		revertir: async (i) => (await axiosInstance.post(item(i) + "/_revert")).data,
		ordenar: async (ids) => (await axiosInstance.post(b + "/_items/_order", { ids })).data,
		subida: async (d) => (await axiosInstance.post(b + "/_uploadUrl", { ...d, purpose: "adjunto" })).data,
		archivo: async (i, pos) => (await axiosInstance.get(item(i) + "/_files/" + pos + "/_url")).data,
		esquema: async () => (await axiosInstance.get(b + "/_scheme")).data,
		guardarEsquema: async (categories, allowTeacherOverride) =>
			(await axiosInstance.put(b + "/_scheme", { categories, ...(allowTeacherOverride === undefined ? {} : { allowTeacherOverride }) })).data,
		quitarEsquema: async () => (await axiosInstance.delete(b + "/_scheme")).data,
		permiteMaestro: async (value) => (await axiosInstance.put(b + "/_teacherOverride", { value })).data,
	}
}
